/**
 * LenderSelector — searchable lender picker with logo previews.
 *
 * Modes:
 *   - Trigger button showing the currently selected lender (with logo)
 *   - Click opens a dropdown listing all active lenders, grouped:
 *       Recent (localStorage) → Featured → All (alphabetical)
 *   - Search box filters across name/displayName/legalName/slug
 *   - Keyboard: Up/Down navigate, Enter select, Esc close
 *   - Optional finance filter (consumer|commercial|all) narrows the list
 *
 * Persists last-5 recently-picked lender slugs in localStorage.
 */
import { useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Check, ChevronDown, Search, Star, X, Clock } from "lucide-react"
import { lendersApi, type Lender } from "@/lib/api"
import { LenderLogo } from "@/components/LenderLogo"
import { cn } from "@/lib/utils"

const RECENT_KEY = "rccrm.recentLenders"
const RECENT_MAX = 5

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : []
  } catch {
    return []
  }
}
function saveRecent(slug: string): void {
  try {
    const cur = loadRecent().filter((s) => s !== slug)
    cur.unshift(slug)
    localStorage.setItem(RECENT_KEY, JSON.stringify(cur.slice(0, RECENT_MAX)))
  } catch {
    /* ignore */
  }
}

type Props = {
  /** Currently selected lender ID (nullable). */
  value: string | null
  /** Called with the new lender when selected, or null when cleared. */
  onChange: (lender: Lender | null) => void
  /** Filter list to consumer or commercial products only. */
  financeFilter?: "consumer" | "commercial" | "all"
  disabled?: boolean
  clearable?: boolean
  className?: string
  /** Optional placeholder shown when nothing is selected. */
  placeholder?: string
}

export function LenderSelector({
  value,
  onChange,
  financeFilter = "all",
  disabled,
  clearable = true,
  className,
  placeholder = "Select a lender…",
}: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [highlightIdx, setHighlightIdx] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const list = useQuery({
    queryKey: ["lenders", { active: "true", finance: financeFilter }],
    queryFn: () =>
      lendersApi.list({ active: "true", finance: financeFilter, sort: "sort_order" }),
    staleTime: 60_000,
  })

  const allLenders = list.data?.items ?? []
  const selected = allLenders.find((l) => l.id === value) ?? null

  // Filter by search query
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return allLenders
    return allLenders.filter((l) =>
      [l.name, l.displayName, l.legalName, l.slug]
        .filter(Boolean)
        .some((s) => s!.toLowerCase().includes(q))
    )
  }, [allLenders, query])

  // Group: Recent → Featured → Rest
  const groups = useMemo(() => {
    const recent = loadRecent()
    const bySlug = new Map(filtered.map((l) => [l.slug, l]))
    const recentPicks = recent
      .map((s) => bySlug.get(s))
      .filter((l): l is Lender => !!l)
    const recentIds = new Set(recentPicks.map((l) => l.id))
    const featured = filtered.filter((l) => l.isFeatured && !recentIds.has(l.id))
    const featuredIds = new Set(featured.map((l) => l.id))
    const rest = filtered.filter((l) => !recentIds.has(l.id) && !featuredIds.has(l.id))
    // Alphabetical within Rest
    rest.sort((a, b) => a.name.localeCompare(b.name))
    return { recent: recentPicks, featured, rest }
  }, [filtered])

  // Flat list of items in display order — used for keyboard nav
  const flatItems = useMemo(
    () => [...groups.recent, ...groups.featured, ...groups.rest],
    [groups]
  )

  // Reset highlight when list changes
  useEffect(() => {
    setHighlightIdx(0)
  }, [query, financeFilter, allLenders.length])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", onDoc)
    return () => document.removeEventListener("mousedown", onDoc)
  }, [open])

  // Focus search when opening
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30)
  }, [open])

  const pick = (l: Lender) => {
    saveRecent(l.slug)
    onChange(l)
    setOpen(false)
    setQuery("")
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false)
      return
    }
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setHighlightIdx((i) => Math.min(i + 1, flatItems.length - 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setHighlightIdx((i) => Math.max(i - 1, 0))
    } else if (e.key === "Enter") {
      e.preventDefault()
      const item = flatItems[highlightIdx]
      if (item) pick(item)
    }
  }

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {selected ? (
        <div
          className={cn(
            "flex items-center gap-3 rounded-md border border-input bg-background px-2 py-1.5",
            !disabled && "hover:border-rc-blue-300 cursor-pointer"
          )}
          onClick={() => !disabled && setOpen((o) => !o)}
        >
          <LenderLogo
            name={selected.name}
            logoUrl={selected.logoUrl}
            size="sm"
          />
          <div className="min-w-0 flex-1">
            <div className="text-foreground truncate text-sm font-medium">
              {selected.displayName ?? selected.name}
            </div>
            <div className="text-muted-foreground truncate text-[11px]">
              {selected.consumerEnabled && selected.commercialEnabled
                ? "Consumer & Commercial"
                : selected.consumerEnabled
                  ? "Consumer"
                  : selected.commercialEnabled
                    ? "Commercial"
                    : "—"}
            </div>
          </div>
          {clearable && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onChange(null)
              }}
              className="text-muted-foreground hover:text-foreground p-1"
              aria-label="Clear lender"
              disabled={disabled}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <ChevronDown className="text-muted-foreground h-4 w-4" />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => !disabled && setOpen((o) => !o)}
          disabled={disabled}
          className={cn(
            "flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm",
            disabled ? "opacity-50" : "hover:border-rc-blue-300 cursor-pointer"
          )}
        >
          <span className="text-muted-foreground">{placeholder}</span>
          <ChevronDown className="text-muted-foreground h-4 w-4" />
        </button>
      )}

      {open && (
        <div
          className="absolute left-0 right-0 z-40 mt-1 max-h-96 overflow-hidden rounded-md border border-border bg-white shadow-lg"
          onKeyDown={onKeyDown}
        >
          <div className="border-border border-b p-2">
            <div className="relative">
              <Search className="text-muted-foreground absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Search lenders…"
                className="border-input h-8 w-full rounded border pl-8 pr-2 text-sm outline-none focus:border-rc-blue-400"
              />
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {list.isLoading ? (
              <div className="text-muted-foreground p-3 text-sm">Loading lenders…</div>
            ) : flatItems.length === 0 ? (
              <div className="text-muted-foreground p-3 text-sm">No lenders match.</div>
            ) : (
              <>
                {groups.recent.length > 0 && (
                  <Group title="Recent" icon={Clock}>
                    {groups.recent.map((l, i) => (
                      <Option
                        key={l.id}
                        lender={l}
                        selected={selected?.id === l.id}
                        highlighted={highlightIdx === i}
                        onClick={() => pick(l)}
                      />
                    ))}
                  </Group>
                )}
                {groups.featured.length > 0 && (
                  <Group title="Featured" icon={Star}>
                    {groups.featured.map((l, i) => {
                      const idx = groups.recent.length + i
                      return (
                        <Option
                          key={l.id}
                          lender={l}
                          selected={selected?.id === l.id}
                          highlighted={highlightIdx === idx}
                          onClick={() => pick(l)}
                        />
                      )
                    })}
                  </Group>
                )}
                {groups.rest.length > 0 && (
                  <Group title="All Lenders">
                    {groups.rest.map((l, i) => {
                      const idx = groups.recent.length + groups.featured.length + i
                      return (
                        <Option
                          key={l.id}
                          lender={l}
                          selected={selected?.id === l.id}
                          highlighted={highlightIdx === idx}
                          onClick={() => pick(l)}
                        />
                      )
                    })}
                  </Group>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function Group({
  title,
  icon: Icon,
  children,
}: {
  title: string
  icon?: typeof Clock
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="text-muted-foreground bg-slate-50 flex items-center gap-1 px-3 py-1 text-[10px] font-medium uppercase tracking-wide">
        {Icon && <Icon className="h-3 w-3" />}
        {title}
      </div>
      <ul>{children}</ul>
    </div>
  )
}

function Option({
  lender,
  selected,
  highlighted,
  onClick,
}: {
  lender: Lender
  selected: boolean
  highlighted: boolean
  onClick: () => void
}) {
  const products = [
    lender.consumerEnabled && "Consumer",
    lender.commercialEnabled && "Commercial",
  ]
    .filter(Boolean)
    .join(" & ")
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        onMouseEnter={(e) => e.currentTarget.focus()}
        className={cn(
          "flex w-full items-center gap-3 px-3 py-2 text-left transition-colors",
          highlighted ? "bg-rc-blue-50" : "hover:bg-slate-50"
        )}
      >
        <LenderLogo name={lender.name} logoUrl={lender.logoUrl} size="sm" />
        <div className="min-w-0 flex-1">
          <div className="text-foreground truncate text-sm font-medium">
            {lender.displayName ?? lender.name}
          </div>
          <div className="text-muted-foreground truncate text-[11px]">{products || "—"}</div>
        </div>
        {selected && <Check className="h-4 w-4 text-rc-blue-600 shrink-0" />}
      </button>
    </li>
  )
}
